import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseDto } from '@models/base/baseDto.model';
import { Genericos } from '@models/base/genericos.model';
import { RangoFechasValores, TiposComprobantes } from '@models/base/identificadores.model';
import { ComprobanteFilterDto } from '@models/comprobantes/comprobanteFilterDto.model';
import { ContratoDetailDto } from '@models/contrato/contratoDetailDto.model';
import { InquilinoDto } from '@models/inquilino/inquilinoDto.model';
import { PersonaPropietarioDto } from '@models/propietarios/personaPropietarioDto.model';
import { ContratosService } from '@services/contratos/contratos.service';
import { EstadosComprobanteService } from '@services/estados-comprobante/estados-comprobante.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { TiposComprobantesService } from '@services/tipos-comprobantes/tipos-comprobantes.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';

@Component({
  selector: 'app-filtro-comprobantes',
  templateUrl: './filtro-comprobantes.component.html',
  styleUrls: ['./filtro-comprobantes.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe]
})
export class FiltroComprobantesComponent {

  @Input() numeroContrato!: number;
  @Input() idTipoComprobante!: number;
  @Output() onFiltrar = new EventEmitter<ComprobanteFilterDto>();
  @Output() onLimpiar = new EventEmitter<void>();

  form!: FormGroup;
  lstRangoFecha = Genericos.lstRangoFecha;
  lstTiposComprobantes!: BaseDto[];
  lstEstadosComprobante!: BaseDto[];
  checkedFiltroAvanzado: boolean = false;
  rangoFecha: number = RangoFechasValores.Trimestral;

  personaPropietarioDto!: PersonaPropietarioDto | null;
  propiedadBaseDto!: BaseDto | null;
  inquilinoDto!: InquilinoDto | null;
  contratoDetailDto!: ContratoDetailDto | null;

  constructor(
    private formBuilder: FormBuilder,
    private datepipe: DatePipe,
    private spinner: NgxSpinnerService,
    private snackBarService: SnackBarService,
    private tiposComprobantesService: TiposComprobantesService,
    private estadosComprobanteService: EstadosComprobanteService,
    private contratoService: ContratosService
  ) {
    this.form = this.formBuilder.group({
      NumeroComprobante: [''],
      IdContrato: [''],
      FechaDesde: [new Date((new Date().getTime() - RangoFechasValores.Bimestral)), Validators.required],
      FechaHasta: [new Date(), Validators.required],
      RangoFecha: [''],
      TiposComprobantes: [''],
      NumeroContrato: [this.contratoDetailDto?.numeroContrato || this.numeroContrato],
      EstadosComprobante: ['']
    });
  }

ngOnInit(): void {
  // Setear valores de los Inputs
  if (!isFalsy(this.idTipoComprobante)) {
    this.form.controls["TiposComprobantes"].setValue(this.idTipoComprobante);
  }

  if (!isFalsy(this.numeroContrato)) {
    this.form.controls["NumeroContrato"].setValue(this.numeroContrato);

    // Dar tiempo para que el form se estabilice
    setTimeout(() => {
      this.buscarComprobantes(this.numeroContrato);
    }, 0);
  }
}

  filtrosAvanzados(event: any): void {
    this.checkedFiltroAvanzado = event.checked;

    if (this.checkedFiltroAvanzado) {
      this.getAllTiposComprobantes();
      this.getAllEstadosComprobante();
    }
  }

  selectRangoFecha(event: any): void {
    this.rangoFecha = event.value;

    if (this.rangoFecha == RangoFechasValores.Todos) {
      this.form.controls["FechaDesde"].setValue(new Date((new Date().getTime() - this.rangoFecha)));
      this.form.controls["FechaHasta"].setValue(new Date((new Date().getTime() + this.rangoFecha)));
    } else {
      this.form.controls["FechaDesde"].setValue(new Date((new Date().getTime() - this.rangoFecha)));
      this.form.controls["FechaHasta"].setValue(new Date());
    }
  }

  selectPropietario(personaPropietarioDto: PersonaPropietarioDto): void {
    this.personaPropietarioDto = personaPropietarioDto;
  }

  selectContrato(baseDto: BaseDto): void {
    if (isFalsy(baseDto)) {
      this.contratoDetailDto = null;
    } else {
      this.getContrato(baseDto.id);
    }
  }

  selectPropiedad(propiedadBaseDto: BaseDto): void {
    this.propiedadBaseDto = propiedadBaseDto;
  }

  selectInquilino(inquilinoDto: InquilinoDto): void {
    this.inquilinoDto = inquilinoDto;
  }

  buscarComprobantes(nroContrato?: number): void {
    let numeroContrato: number | null = null;

    // Ajustar fechas
    let fechaDesde = this.datepipe.transform(this.form.controls["FechaDesde"].value, 'yyyy-MM-dd') + ' 00:00:00';
    let fechaHasta = this.datepipe.transform(this.form.controls["FechaHasta"].value, 'yyyy-MM-dd') + ' 23:59:59';

    const fechaDesdeValue = this.form.controls["FechaDesde"].value;
    const fechaHastaValue = this.form.controls["FechaHasta"].value;


    // Variables del filtro
    let idContrato = isFalsy(this.contratoDetailDto) ? null : this.contratoDetailDto.id;
    let idInquilino = isFalsy(this.inquilinoDto) ? null : this.inquilinoDto.id;
    let idPropiedad = isFalsy(this.propiedadBaseDto) ? null : this.propiedadBaseDto.id;
    let idPropietario = isFalsy(this.personaPropietarioDto) ? null : this.personaPropietarioDto.id_Propietario;

    if (!isFalsy(nroContrato)) {
      numeroContrato = nroContrato;
    } else {
      numeroContrato = isFalsy(this.contratoDetailDto) ? null : this.contratoDetailDto.numeroContrato;
    }

    let nroComprobante = this.form.controls["NumeroComprobante"].value || null;

    let idTipoComprobante = !isFalsy(this.idTipoComprobante)
      ? this.idTipoComprobante
      : (this.form.controls["TiposComprobantes"].value || null);

    let idEstadoComprobante = this.form.controls["EstadosComprobante"].value || null;

    const filtro: ComprobanteFilterDto = {
      fechaDesde: fechaDesde!,
      fechaHasta: fechaHasta!,
      numeroContrato,
      nroComprobante,
      idTipoComprobante,
      idEstadoComprobante,
      idPropiedad,
      idInquilino,
      idPropietario,
      idContrato
    };

    this.onFiltrar.emit(filtro);
  }

  clearForm(): void {
    this.form.controls["EstadosComprobante"].setValue(0);
    this.form.controls["NumeroComprobante"].setValue("");
    this.form.controls["NumeroContrato"].setValue("");

    if (!isFalsy(this.idTipoComprobante)) {
      this.form.controls["TiposComprobantes"].setValue(this.idTipoComprobante);
    } else {
      this.form.controls["TiposComprobantes"].setValue(0);
    }

    this.inquilinoDto = null;
    this.propiedadBaseDto = null;
    this.personaPropietarioDto = null;
    this.contratoDetailDto = new ContratoDetailDto();

    this.onLimpiar.emit();
  }

  getContrato(id: number): void {
    this.spinner.show("spMovimiento");
    this.contratoService.getById(id).subscribe(
      data => {
        this.spinner.hide("spMovimiento");
        this.contratoDetailDto = data;
      },
      error => {
        this.spinner.hide("spMovimiento");
        this.snackBarService.showError(error, "Error");
      }
    );
  }

  getAllTiposComprobantes(): void {
    this.spinner.show("spBusquedaTiposComprobantes");
    this.tiposComprobantesService.getAll().subscribe(
      data => {
        this.spinner.hide("spBusquedaTiposComprobantes");
        this.lstTiposComprobantes = data;
      },
      error => {
        this.spinner.hide("spBusquedaTiposComprobantes");
        this.snackBarService.showError(error, "Error");
      }
    );
  }

  getAllEstadosComprobante(): void {
    this.spinner.show("spBusquedaEstadosComprobante");
    this.estadosComprobanteService.getAll().subscribe(
      data => {
        this.spinner.hide("spBusquedaEstadosComprobante");
        this.lstEstadosComprobante = data;
      },
      error => {
        this.spinner.hide("spBusquedaEstadosComprobante");
        this.snackBarService.showError(error, "Error");
      }
    );
  }

  trackByItems(index: number, item: any): any {
    return item;
  }
}
